import { FC, forwardRef, InputHTMLAttributes, LegacyRef, ReactNode } from 'react';
import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color'> &
  Pick<InputProps, 'size' | 'color' | 'bordered'> & {
    label?: string;
    error?: string;
    wrapperClassName?: string;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
  } & { ref?: LegacyRef<HTMLInputElement> };

const TextInput: FC<TextInputProps> = forwardRef(
  (
    { label, error, wrapperClassName, startIcon, endIcon, size = 'md', color, bordered = true, onChange, ...args },
    ref
  ) => {
    return (
      <div className={cn('form-control max-w-full w-full', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span className="label-text">{label}</span>
          </label>
        ) : null}
        <label
          className={cn('input flex items-center justify-between max-w-full gap-2', {
            'input-ghost': color === 'ghost',
            'input-primary': color === 'primary',
            'input-secondary': color === 'secondary',
            'input-accent': color === 'accent',
            'input-info': color === 'info',
            'input-success': color === 'success',
            'input-warning': color === 'warning',
            'input-error': error || color === 'error',
            'input-xs': size === 'xs',
            'input-sm': size === 'sm',
            'input-md': size === 'md' || size,
            'input-lg': size === 'lg',
            'input-bordered': bordered,
          })}
        >
          {startIcon ? startIcon : null}
          <input
            className={cn('grow overflow-auto', {
              'max-w-full': !startIcon && !endIcon,
              'max-w-[calc(100%-1rem)]': !!startIcon || !!endIcon,
              'max-w-[calc(100%-2rem)]': !!startIcon && !!endIcon,
            })}
            ref={ref}
            onChange={(e) => {
              if (args.type === 'number') {
                if (args.max) {
                  if (Number(e.target.value) > Number(args.max)) {
                    e.target.value = `${args.max}`;
                  }
                }
                if (args.min) {
                  if (Number(e.target.value) < Number(args.min)) {
                    e.target.value = `${args.min}`;
                  }
                }
              }

              if (onChange) {
                onChange(e);
              }
            }}
            {...args}
          />
          {endIcon ? endIcon : null}
        </label>
        {error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
);

export default TextInput;
